import { Button } from "@/components/ui/button";
import { FilterChip } from "@/components/ui/filter-chip";
import type { BrowseLicense } from "@/lib/browse-url-state";
import { BROWSE_DEFAULTS } from "@/lib/browse-url-state";

interface ActiveFiltersProps {
  selectedCategory: string | null;
  categoryName: string | null;
  priceRange: [number, number];
  minRating: number;
  license: BrowseLicense;
  search: string;
  selectedTags: string[];
  onClearCategory: () => void;
  onClearPriceRange: () => void;
  onClearRating: () => void;
  onClearLicense: () => void;
  onClearSearch: () => void;
  onRemoveTag: (tag: string) => void;
  onClearAll: () => void;
}

export function ActiveFilters({
  selectedCategory,
  categoryName,
  priceRange,
  minRating,
  license,
  search,
  selectedTags,
  onClearCategory,
  onClearPriceRange,
  onClearRating,
  onClearLicense,
  onClearSearch,
  onRemoveTag,
  onClearAll,
}: ActiveFiltersProps) {
  const hasFilters =
    selectedCategory ||
    priceRange[0] > BROWSE_DEFAULTS.minPrice ||
    priceRange[1] < BROWSE_DEFAULTS.maxPrice ||
    minRating > 0 ||
    license !== "all" ||
    search ||
    selectedTags.length > 0;

  if (!hasFilters) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-border/50 bg-card/30 backdrop-blur-sm">
      <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Active:</span>

      {search && <FilterChip label={`"${search}"`} selected onRemove={onClearSearch} />}

      {selectedTags.map((tag) => (
        <FilterChip key={tag} label={`#${tag}`} selected onRemove={() => onRemoveTag(tag)} />
      ))}

      {selectedCategory && categoryName && (
        <FilterChip label={categoryName} selected onRemove={onClearCategory} />
      )}

      {license === "free" && <FilterChip label="Free only" selected onRemove={onClearLicense} />}

      {license === "paid" && <FilterChip label="Paid only" selected onRemove={onClearLicense} />}

      {license !== "free" && (priceRange[0] > BROWSE_DEFAULTS.minPrice || priceRange[1] < BROWSE_DEFAULTS.maxPrice) && (
        <FilterChip label={`$${priceRange[0]} - $${priceRange[1]}+`} selected onRemove={onClearPriceRange} />
      )}

      {minRating > 0 && (
        <FilterChip label={`${minRating}+ Stars`} selected onRemove={onClearRating} />
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive ml-auto"
      >
        Clear all
      </Button>
    </div>
  );
}
